import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { useState } from 'react';
import { useCotiPluginContext } from '@/context/PrivacyBridgeContext';
import { useBlockscoutTransactions } from '@/hooks/useBlockscoutTransactions';
import { useContractDeploymentDate } from '@/hooks/useContractDeploymentDate';
import { useContractAdmin } from '@/hooks/useContractAdmin';
import { getExplorerBaseUrl, truncateAddress } from '@/lib/blockscout';
import { resolveBridgeStatsMeta } from '@/lib/explorerApi';
import { aggregateTransactionsByDay, aggregateVolumeByDay, groupStats, groupVolume, ChartPeriod } from '@/lib/txAggregation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { useChartColors } from '@/hooks/useChartColors';

const ADDRESS_REGEX = /^0x[0-9a-fA-F]{40}$/;

interface LocationState {
  bridgeName?: string;
  tokenSymbol?: string;
  tokenDecimals?: number;
  chainId?: number;
}

const BridgeStatisticsPage = () => {
  const { bridgeAddress = '' } = useParams<{ bridgeAddress: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { chainId } = useCotiPluginContext();

  const state = (location.state as LocationState) ?? {};
  const walletChainId = chainId ? parseInt(chainId) : 7082400;
  const preferredChainId = state.chainId ?? walletChainId;

  // Validate address before any API calls
  const isValidAddress = ADDRESS_REGEX.test(bridgeAddress);

  // Prefer chain-config lookup so hard refresh still gets correct decimals/symbol.
  const resolved = isValidAddress
    ? resolveBridgeStatsMeta(bridgeAddress, preferredChainId)
    : null;

  const statsChainId = resolved?.chainId ?? (isValidAddress ? preferredChainId : null);
  const bridgeName = resolved?.bridgeName ?? state.bridgeName ?? 'Unknown';
  const tokenSymbol = resolved?.tokenSymbol ?? state.tokenSymbol ?? 'Unknown';
  const tokenDecimals = resolved?.tokenDecimals ?? state.tokenDecimals ?? 18;
  const chartColors = useChartColors();

  let explorerBaseUrl = '';
  try {
    explorerBaseUrl = getExplorerBaseUrl(statsChainId ?? walletChainId);
  } catch {
    explorerBaseUrl = 'https://testnet.cotiscan.io';
  }

  const walletMismatch =
    isValidAddress &&
    statsChainId != null &&
    walletChainId !== statsChainId;

  // Hooks are always called (React rules), but with sentinel values when address is invalid
  // so no real API/RPC calls are made for invalid addresses.
  const DUMMY_ADDRESS = '0x0000000000000000000000000000000000000000';
  const safeAddress = isValidAddress ? bridgeAddress : DUMMY_ADDRESS;
  const safeChainId = isValidAddress ? statsChainId : null;

  const {
    transactions,
    isLoading: txLoading,
    error: txError,
    retry: txRetry,
  } = useBlockscoutTransactions(safeChainId, safeAddress);

  const {
    deploymentDate,
    isLoading: deploymentLoading,
    error: deploymentError,
  } = useContractDeploymentDate(safeChainId, safeAddress);

  const {
    adminAddress,
    isLoading: adminLoading,
    error: adminError,
  } = useContractAdmin(safeChainId, safeAddress);

  const chartData = aggregateTransactionsByDay(transactions, bridgeAddress);
  const volumeData = aggregateVolumeByDay(transactions, bridgeAddress, tokenDecimals);
  const [period, setPeriod] = useState<ChartPeriod>('day');
  const [volumePeriod, setVolumePeriod] = useState<ChartPeriod>('day');
  const displayData = groupStats(chartData, period);
  const displayVolume = groupVolume(volumeData, volumePeriod);

  const PERIODS: { label: string; value: ChartPeriod }[] = [
    { label: 'Day', value: 'day' },
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
  ];

  const periodLabel: Record<ChartPeriod, string> = {
    day: 'Daily',
    week: 'Weekly',
    month: 'Monthly',
  };

  if (!isValidAddress) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar showAdminBadge />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <Alert variant="destructive" className="glass">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Invalid bridge address: <code className="font-mono">{bridgeAddress}</code>. The address must be a valid Ethereum address (0x followed by 40 hex characters).
              </AlertDescription>
            </Alert>
            <div className="mt-4">
              <Link to="/backoffice" className="text-primary hover:underline flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar showAdminBadge />
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/backoffice')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
            </div>
          </div>

          {walletMismatch && (
            <Alert className="glass">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Showing statistics for this bridge&apos;s network (chain {statsChainId}). Your wallet is on chain {walletChainId}.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-glow">{bridgeName}</h1>
            {tokenSymbol !== 'Unknown' && (
              <Badge variant="outline" className="bg-primary/10 text-sm">
                {tokenSymbol}
              </Badge>
            )}
          </div>

          {/* Chart Section */}
          <Card className="glass box-glow">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle>
                  Number of {periodLabel[period]} Deposits &amp; Withdrawals Txs
                </CardTitle>
                <div className="flex items-center gap-1">
                  {PERIODS.map(({ label, value }) => (
                    <Button
                      key={value}
                      variant={period === value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPeriod(value)}
                      className="h-7 px-3 text-xs"
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {txLoading ? (
                <Skeleton className="h-64 w-full" />
              ) : txError ? (
                <div className="space-y-3">
                  <Alert variant="destructive" className="glass">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{txError}</AlertDescription>
                  </Alert>
                  <Button variant="outline" size="sm" onClick={txRetry}>
                    Retry
                  </Button>
                </div>
              ) : displayData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No transaction data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={displayData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: chartColors.axis }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: chartColors.axis }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: chartColors.tooltipBg,
                        borderColor: chartColors.tooltipBorder,
                        color: chartColors.axis,
                      }}
                    />
                    <Legend wrapperStyle={{ color: chartColors.axis }} />
                    <Bar dataKey="deposits" name="Deposits" fill={chartColors.deposit} />
                    <Bar dataKey="withdrawals" name="Withdrawals" fill={chartColors.withdrawal} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Volume Chart Section */}
          <Card className="glass box-glow">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle>Total Volume of Deposits &amp; Withdrawals ({tokenSymbol})</CardTitle>
                <div className="flex items-center gap-1">
                  {PERIODS.map(({ label, value }) => (
                    <Button
                      key={value}
                      variant={volumePeriod === value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setVolumePeriod(value)}
                      className="h-7 px-3 text-xs"
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {txLoading ? (
                <Skeleton className="h-64 w-full" />
              ) : txError ? (
                <div className="space-y-3">
                  <Alert variant="destructive" className="glass">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{txError}</AlertDescription>
                  </Alert>
                  <Button variant="outline" size="sm" onClick={txRetry}>
                    Retry
                  </Button>
                </div>
              ) : displayVolume.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No transaction data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={displayVolume} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: chartColors.axis }} />
                    <YAxis allowDecimals={true} tick={{ fontSize: 12, fill: chartColors.axis }} />
                    <Tooltip
                      formatter={(v: number) => `${v.toFixed(4)} ${tokenSymbol}`}
                      contentStyle={{
                        backgroundColor: chartColors.tooltipBg,
                        borderColor: chartColors.tooltipBorder,
                        color: chartColors.axis,
                      }}
                    />
                    <Legend wrapperStyle={{ color: chartColors.axis }} />
                    <Bar dataKey="depositVolume" name="Deposit Volume" fill={chartColors.deposit} />
                    <Bar dataKey="withdrawalVolume" name="Withdrawal Volume" fill={chartColors.withdrawal} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Stat Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Deployment Date Card */}
            <Card className="glass box-glow">
              <CardHeader>
                <CardTitle className="text-sm font-medium">Deployment Date</CardTitle>
              </CardHeader>
              <CardContent>
                {deploymentLoading ? (
                  <Skeleton className="h-6 w-40" />
                ) : (
                  <p className="text-lg font-semibold">
                    {deploymentDate ?? (deploymentError ? 'Unknown' : 'Unknown')}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Admin Address Card */}
            <Card className="glass box-glow">
              <CardHeader>
                <CardTitle className="text-sm font-medium">Contract Administrator</CardTitle>
              </CardHeader>
              <CardContent>
                {adminLoading ? (
                  <Skeleton className="h-6 w-40" />
                ) : adminAddress ? (
                  <a
                    href={`${explorerBaseUrl}/address/${adminAddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-mono text-sm"
                  >
                    {truncateAddress(adminAddress)}
                  </a>
                ) : (
                  <p className="text-lg font-semibold text-muted-foreground">
                    {adminError ? 'Unknown' : 'Unknown'}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BridgeStatisticsPage;
